import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { format, isWithinInterval } from "date-fns";

import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Fade,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import FormField from "layouts/pages/account/components/FormField";

import { setDataConfig, setMessage, useMaterialUIController } from "context";

import { IEventoResponse } from "interfaces/eventos";
import { ILotes, TagLote, ValoresTag } from "interfaces/lotes";
import { TipoTag } from "interfaces/typesystem";
import {
  deleteTagLoteEvento,
  editLoteEvento,
  editTagLoteEvento,
  saveLoteEvento,
  saveTagLoteEvento,
} from "services/lotes";
import { saveTipoTag, deleteTipoTag, listTipoTags } from "services/tipoTags";
import { formatDateEndOfDay } from "helpers/formatDate";
import TagColorChip from "components/TagColorChip";
import { resolveTagColorHex } from "helpers/getColorsTags";
import {
  currencyMaskBRL,
  currencyToNumber,
  formatCurrency,
} from "helpers/utils";
import {
  colorsCupons,
  keyLocalStorage,
  OPTIONTIPOINSCRICAO,
} from "helpers/constants";

interface Props {
  isEdit: boolean;
  evento: IEventoResponse;
  loteSelected: ILotes | null;
  handleSetLoteSelected: (lote: ILotes | null) => void;
  updateEvento: () => void;
  open: boolean;
  handleClose: () => void;
}

type TagOldProps = {
  id: number;
  lotesEventosId: number;
  tag: {
    id: number;
    tipo: string;
    cor?: string | null;
  };
  valor: string;
};
type Tag = { id: number; tipo: string; cor?: string | null; tagOld?: TagOldProps };

const TAG_ACCENT = "#F96030";

export default function ModalAddLote({
  isEdit,
  open,
  evento,
  loteSelected,
  handleSetLoteSelected,
  updateEvento,
  handleClose,
}: Props) {
  const [controller, dispatch] = useMaterialUIController();
  const { dataConfig } = controller;
  const listTag: TipoTag[] = dataConfig?.tipoTags || [];

  const [selectTag, setSelectTag] = useState<Tag | null>(null);
  const [valueTag, setValueTag] = useState<string>("");
  const [isEditTag, setIsEditTag] = useState<boolean>(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagQtd, setNewTagQtd] = useState("0");
  const [newTagCor, setNewTagCor] = useState<string>(colorsCupons[1]);
  const [creatingTag, setCreatingTag] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState<number | null>(null);
  const [showCreateTagForm, setShowCreateTagForm] = useState(false);
  const [showDeleteTagsMode, setShowDeleteTagsMode] = useState(false);

  const inicioInscricao = evento?.inicioIncricao.slice(0, 10);
  const finalInscricao = evento?.fimInscricao.slice(0, 10);

  const dtInicioLote =
    loteSelected && loteSelected.dataInicio
      ? formatDateEndOfDay(loteSelected.dataInicio, "yyyy-MM-dd")
      : null;

  const dtfimLote =
    loteSelected && loteSelected.dataFinal
      ? formatDateEndOfDay(loteSelected.dataFinal, "yyyy-MM-dd")
      : null;

  const listTagLote =
    loteSelected && loteSelected.valoresTags ? loteSelected.valoresTags : [];

  const tagsAlreadyAdded = useMemo(
    () => new Set(listTagLote.map((item) => item.tag.id)),
    [listTagLote]
  );

  const resolveCorTag = (tag: {
    id?: number;
    tipo: string;
    cor?: string | null;
  }) => {
    // Prioriza o catálogo atualizado (evita cor antiga em cache do evento)
    const fromCatalog = listTag.find(
      (item) =>
        (tag.id != null && item.id === tag.id) ||
        item.tipo.toLowerCase() === tag.tipo.toLowerCase()
    );
    return resolveTagColorHex(tag.tipo, fromCatalog?.cor ?? tag.cor);
  };

  const handleValueTag = (event: ChangeEvent<HTMLInputElement>) => {
    setValueTag(currencyMaskBRL(event.target.value));
  };

  const resetTagForm = () => {
    setSelectTag(null);
    setValueTag("");
    setIsEditTag(false);
  };

  const resetCreateTagForm = () => {
    setShowCreateTagForm(false);
    setNewTagName("");
    setNewTagQtd("0");
    setNewTagCor(colorsCupons[1]);
  };

  const persistTipoTagsConfig = (updatedTags: TipoTag[]) => {
    if (!dataConfig) return;
    const updatedData = { ...dataConfig, tipoTags: updatedTags };
    setDataConfig(dispatch, updatedData);
    localStorage.setItem(
      keyLocalStorage.DEFAULT_CONSTANTS,
      JSON.stringify(updatedData)
    );
  };

  // Atualiza o catálogo de tags (com cores) ao abrir o modal
  useEffect(() => {
    if (!open || !dataConfig) return;

    let cancelled = false;
    listTipoTags()
      .then((tags) => {
        if (cancelled) return;
        persistTipoTagsConfig(tags);
      })
      .catch(() => {
        // Mantém o cache local se a listagem falhar
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleEditValueTag = (tag: ValoresTag) => {
    setIsEditTag(true);
    const cents = Math.round(Number(tag.valor) * 100);
    setValueTag(currencyMaskBRL(String(Number.isFinite(cents) ? cents : 0)));
    setSelectTag({
      id: tag.tag.id,
      tipo: tag.tag.tipo,
      cor: tag.tag.cor,
      tagOld: tag,
    });
  };

  const handleSelectTag = (itemSelected: Tag) => {
    if (isEditTag) return;

    const existing = listTagLote.find(
      (item) => item.tag.id === itemSelected.id
    );
    if (existing) {
      handleEditValueTag(existing);
      return;
    }

    if (selectTag?.id === itemSelected.id) {
      setSelectTag(null);
    } else {
      setSelectTag({
        id: itemSelected.id,
        tipo: itemSelected.tipo,
        cor: itemSelected.cor,
      });
    }
  };

  const checkExistsTagCategory = (tag: Tag) => {
    const categoryEvento = evento.distanciaEvento;

    return categoryEvento.some((distancia) =>
      distancia.categoriaEvento.some(
        (categoria) => categoria.tag.tipo === tag.tipo
      )
    );
  };

  const handleCreateTipoTag = async () => {
    const tipo = newTagName.trim();
    if (tipo.length < 2) {
      setMessage(dispatch, {
        open: true,
        text: "Informe um nome com pelo menos 2 caracteres para a nova tag.",
        severity: "error",
      });
      return;
    }

    const alreadyExists = listTag.some(
      (item) => item.tipo.toLowerCase() === tipo.toLowerCase()
    );
    if (alreadyExists) {
      setMessage(dispatch, {
        open: true,
        text: "Já existe uma tag com este nome na lista.",
        severity: "warning",
      });
      return;
    }

    setCreatingTag(true);
    try {
      const created = await saveTipoTag({
        tipo,
        qtd_atleta_necessario: Number(newTagQtd) || 0,
        cor: newTagCor,
      });
      persistTipoTagsConfig([...listTag, created]);
      resetCreateTagForm();
      setIsEditTag(false);
      setSelectTag({ id: created.id, tipo: created.tipo, cor: created.cor });
      setMessage(dispatch, {
        open: true,
        text: `Tag "${created.tipo}" criada. Informe o valor e adicione ao lote.`,
        severity: "success",
      });
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message || "Não foi possível criar a tag.",
        severity: "error",
      });
    } finally {
      setCreatingTag(false);
    }
  };

  const handleDeleteTipoTag = async (tag: TipoTag) => {
    const nasCategorias = checkExistsTagCategory({
      id: tag.id,
      tipo: tag.tipo,
    });
    const nosLotes = (evento.lotesEventos ?? []).some((lote) =>
      lote.valoresTags.some((valor) => valor.tag.id === tag.id)
    );

    if (nasCategorias || nosLotes) {
      const onde = [
        nasCategorias ? "em categorias" : "",
        nosLotes ? "em lotes" : "",
      ]
        .filter(Boolean)
        .join(" e ");
      setMessage(dispatch, {
        open: true,
        text: `Não é possível excluir a tag "${tag.tipo}". Ela já está em uso ${onde}. Altere as categorias para outra tag e remova a tag dos lotes antes de excluir.`,
        severity: "error",
      });
      return;
    }

    if (
      !window.confirm(
        `Excluir a tag "${tag.tipo}" da lista de disponíveis?`
      )
    )
      return;

    setDeletingTagId(tag.id);
    try {
      await deleteTipoTag(tag.id);
      persistTipoTagsConfig(listTag.filter((item) => item.id !== tag.id));
      if (selectTag?.id === tag.id) resetTagForm();
      setMessage(dispatch, {
        open: true,
        text: `Tag "${tag.tipo}" excluída da lista.`,
        severity: "success",
      });
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message || "Não foi possível excluir a tag.",
        severity: "error",
      });
    } finally {
      setDeletingTagId(null);
    }
  };

  const handleRemoveTag = (tag: ValoresTag) => {
    if (isEdit) {
      if (checkExistsTagCategory(tag.tag)) {
        setMessage(dispatch, {
          open: true,
          text: `Não é permitido excluir a tag '${tag.tag.tipo}' — existem categorias vinculadas.`,
          severity: "error",
        });
        return;
      }
      deleteTagLoteEvento(tag.id, evento.id)
        .then((resp) => {
          updateEvento();
          handleSetLoteSelected(resp.find((item) => item.id === loteSelected.id));
          if (selectTag?.id === tag.tag.id) resetTagForm();
        })
        .catch((error: any) =>
          setMessage(dispatch, {
            open: true,
            text: error.message || "Não foi possível excluir a tag do lote.",
            severity: "error",
          })
        );
    } else {
      const newTags = listTagLote.filter((item) => item.id !== tag.tag.id);
      const updateLote: ILotes = {
        ...loteSelected,
        valoresTags: newTags,
      };
      handleSetLoteSelected(updateLote);
      if (selectTag?.id === tag.tag.id) resetTagForm();
    }
  };

  const handleAddTag = async () => {
    if (!selectTag) {
      setMessage(dispatch, {
        open: true,
        text: "Selecione ou crie uma tag antes de adicionar.",
        severity: "error",
      });
      return;
    }

    const valorTag = currencyToNumber(valueTag);

    if (!valueTag.trim() || Number.isNaN(valorTag) || valorTag < 0) {
      setValueTag("");
      setMessage(dispatch, {
        open: true,
        text: "Favor informar um valor para a tag",
        severity: "error",
      });
      return;
    }

    const newTag: TagLote = {
      eventoId: evento.id,
      lotesEventosId: loteSelected ? loteSelected.id : null,
      tagId: selectTag.id,
      valor: valorTag,
    };

    setSavingTag(true);
    try {
      if (isEdit) {
        if (isEditTag) {
          if (!selectTag.tagOld?.id) {
            throw new Error("Tag selecionada inválida para edição.");
          }
          const resp = await editTagLoteEvento({
            id: selectTag.tagOld.id,
            ...newTag,
          });
          updateEvento();
          handleSetLoteSelected(
            resp.find((item) => item.id === loteSelected.id)
          );
        } else {
          if (tagsAlreadyAdded.has(selectTag.id)) {
            setMessage(dispatch, {
              open: true,
              text: "Esta TAG já foi adicionada",
              severity: "error",
            });
            return;
          }
          const resp = await saveTagLoteEvento(newTag);
          updateEvento();
          handleSetLoteSelected(
            resp.find((item) => item.id === loteSelected.id)
          );
        }
        resetTagForm();
      } else {
        if (listTagLote.find((item) => item.tag.tipo === selectTag.tipo)) {
          setMessage(dispatch, {
            open: true,
            text: "Esta TAG já foi adicionada",
            severity: "error",
          });
          return;
        }

        const localTag: ValoresTag = {
          id: selectTag.id,
          lotesEventosId: loteSelected ? loteSelected.id : selectTag.id,
          tag: {
            id: selectTag.id,
            tipo: selectTag.tipo,
            cor: selectTag.cor ?? resolveCorTag(selectTag),
          },
          valor: valorTag.toString(),
        };

        const updateLote: ILotes = {
          ...loteSelected,
          valoresTags: loteSelected
            ? [...loteSelected.valoresTags, localTag]
            : [localTag],
        };
        handleSetLoteSelected(updateLote);
        resetTagForm();
      }
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message || "Não foi possível salvar a tag do lote.",
        severity: "error",
      });
    } finally {
      setSavingTag(false);
    }
  };

  const handleFormChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;

    const updateLote: ILotes = {
      ...loteSelected,
      [name]: value,
      valoresTags: loteSelected ? [...loteSelected.valoresTags] : [],
    };

    handleSetLoteSelected(updateLote);
  };

  const submitFormAddLote = () => {
    if (!loteSelected) {
      setMessage(dispatch, {
        open: true,
        text: "Para adicionar um lote deve ter data de inicio, data de fim e pelo menos uma tag",
        severity: "error",
      });
      return;
    }

    if (loteSelected?.valoresTags.length === 0) {
      setMessage(dispatch, {
        open: true,
        text: "Para adicionar um lote deve ter pelo menos uma tag",
        severity: "error",
      });
      return;
    }

    if (!checkDataPeriodoInscricao(loteSelected.dataInicio)) {
      setMessage(dispatch, {
        open: true,
        text: "Data de inicio esta fora do período de inscrição",
        severity: "error",
      });
      return;
    }

    if (!checkDataPeriodoInscricao(loteSelected.dataFinal)) {
      setMessage(dispatch, {
        open: true,
        text: "Data de final esta fora do período de inscrição",
        severity: "error",
      });
      return;
    }

    if (isEdit) {
      editLoteEvento({
        eventoId: evento.id,
        id: loteSelected.id,
        dataFinal: loteSelected.dataFinal,
        dataInicio: loteSelected.dataInicio,
      }).then(() => {
        updateEvento();
        handleClose();
      });
    } else {
      saveLoteEvento(evento.id, loteSelected)
        .then(() => {
          updateEvento();
          handleClose();
        })
        .catch((erro: any) => {
          setMessage(dispatch, {
            open: true,
            text: erro.message,
            severity: "error",
          });
        });
    }
  };

  const checkDataPeriodoInscricao = (data: Date | string): boolean => {
    if (!data) return false;
    return isWithinInterval(data, {
      start: inicioInscricao,
      end: finalInscricao,
    });
  };

  const handleBlurCheckDataInscricao = (data: Date | string) => {
    if (!checkDataPeriodoInscricao(data)) {
      setMessage(dispatch, {
        open: true,
        text: "Data de inicio esta fora do período de inscrição",
        severity: "error",
      });
    }
  };

  const handleCloseModal = () => {
    handleClose();
    resetTagForm();
    resetCreateTagForm();
    setShowDeleteTagsMode(false);
  };

  return (
    <Modal open={open} closeAfterTransition onClose={handleCloseModal}>
      <Fade in={open}>
        <MDBox
          sx={{
            ...styleModal,
            width: { xs: "96%", md: "90%", lg: "86%" },
            maxWidth: 1180,
          }}
        >
          {/* Header fixo */}
          <MDBox
            sx={{
              flexShrink: 0,
              px: { xs: 2.5, md: 3.5 },
              pt: 2.5,
              pb: 2,
              position: "relative",
              borderBottom: "1px solid rgba(59,0,80,0.08)",
              background:
                "linear-gradient(135deg, rgba(59,0,80,0.04) 0%, rgba(249,96,48,0.05) 100%)",
              zIndex: 2,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{ position: "absolute", top: 12, right: 12 }}
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>

            <MDTypography
              variant="h4"
              fontWeight="bold"
              sx={{ color: "#3B0050", pr: 5 }}
            >
              {isEdit ? "Alteração" : "Inserção"} do Lote do Evento
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="medium">
              {evento.titulo}
            </MDTypography>
          </MDBox>

          {/* Conteúdo rolável */}
          <MDBox
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: { xs: 2.5, md: 3.5 },
              py: 3,
            }}
          >
            <Alert
              severity="info"
              sx={{
                mb: 2,
                borderRadius: "10px",
                bgcolor: "rgba(59,0,80,0.04)",
                border: "1px solid rgba(59,0,80,0.08)",
                "& .MuiAlert-icon": { color: "#3B0050" },
              }}
            >
              <MDTypography variant="caption" color="text" fontWeight="regular">
                A data do lote deve estar dentro do período de inscrição{" "}
                <b>{`(${format(
                  inicioInscricao.concat("T23:59:59.000Z"),
                  "dd/MM/yyyy"
                )} - ${format(
                  finalInscricao.concat("T23:59:59.000Z"),
                  "dd/MM/yyyy"
                )})`}</b>
              </MDTypography>
            </Alert>

            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={6}>
                <FormField
                  name="dataInicio"
                  type="date"
                  label="Data início lote"
                  onBlur={() => handleBlurCheckDataInscricao(dtInicioLote)}
                  value={dtInicioLote}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField
                  name="dataFinal"
                  type="date"
                  label="Data final lote"
                  value={dtfimLote}
                  onChange={handleFormChange}
                  onBlur={() => handleBlurCheckDataInscricao(dtfimLote)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2.5} alignItems="stretch">
              {/* LEFT — tags already on lot */}
              <Grid item xs={12} md={5}>
                <MDBox
                  sx={{
                    height: "100%",
                    minHeight: { md: 420 },
                    border: "1px solid rgba(59,0,80,0.1)",
                    borderRadius: "14px",
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <MDBox
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      borderBottom: "1px solid rgba(59,0,80,0.08)",
                      background:
                        "linear-gradient(90deg, rgba(59,0,80,0.06), transparent)",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocalOfferOutlinedIcon
                        sx={{ fontSize: 20, color: TAG_ACCENT }}
                      />
                      <MDBox>
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          sx={{ color: "#3B0050", display: "block" }}
                        >
                          Tags do lote
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {listTagLote.length === 0
                            ? "Nenhuma adicionada ainda"
                            : `${listTagLote.length} tag${listTagLote.length > 1 ? "s" : ""} definida${listTagLote.length > 1 ? "s" : ""}`}
                        </MDTypography>
                      </MDBox>
                    </Stack>
                  </MDBox>

                  <MDBox
                    sx={{
                      flex: 1,
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                      overflowY: "auto",
                      maxHeight: { xs: 280, md: 460 },
                      bgcolor: "rgba(59,0,80,0.015)",
                    }}
                  >
                    {listTagLote.length > 0 ? (
                      listTagLote.map((item, idx) => {
                        const isActiveEdit =
                          isEditTag && selectTag?.id === item.tag.id;
                        return (
                          <MDBox
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              px: 1.5,
                              py: 1.25,
                              borderRadius: "12px",
                              bgcolor: "#fff",
                              border: isActiveEdit
                                ? `1.5px solid ${TAG_ACCENT}`
                                : "1px solid rgba(0,0,0,0.08)",
                              boxShadow: isActiveEdit
                                ? "0 0 0 3px rgba(249,96,48,0.12)"
                                : "0 1px 2px rgba(0,0,0,0.03)",
                              transition: "border-color 0.15s, box-shadow 0.15s",
                            }}
                          >
                            <TagColorChip
                              tipo={item.tag.tipo}
                              cor={resolveCorTag(item.tag)}
                              label={`${item.tag.tipo} — ${formatCurrency(item.valor)}`}
                            />
                            <Stack direction="row" spacing={0.25}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditValueTag(item)}
                                title="Editar valor"
                                sx={{
                                  bgcolor: "rgba(59,0,80,0.05)",
                                  "&:hover": { bgcolor: "rgba(59,0,80,0.1)" },
                                }}
                              >
                                <Icon fontSize="small" sx={{ color: "#3B0050" }}>
                                  edit
                                </Icon>
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveTag(item)}
                                title="Remover tag"
                                sx={{
                                  bgcolor: "rgba(211,47,47,0.06)",
                                  "&:hover": {
                                    bgcolor: "rgba(211,47,47,0.12)",
                                  },
                                }}
                              >
                                <Icon fontSize="small" color="error">
                                  delete
                                </Icon>
                              </IconButton>
                            </Stack>
                          </MDBox>
                        );
                      })
                    ) : (
                      <MDBox
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          gap: 1,
                          py: 4,
                          px: 2,
                          borderRadius: "12px",
                          border: "1px dashed rgba(59,0,80,0.2)",
                          bgcolor: "#fff",
                        }}
                      >
                        <LocalOfferOutlinedIcon
                          sx={{ fontSize: 36, color: "rgba(59,0,80,0.25)" }}
                        />
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          color="text"
                        >
                          Nenhuma tag neste lote
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Use o painel ao lado para selecionar ou criar.
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              </Grid>

              {/* RIGHT — select / create / value */}
              <Grid item xs={12} md={7}>
                <MDBox
                  sx={{
                    height: "100%",
                    minHeight: { md: 420 },
                    border: "1px solid rgba(249,96,48,0.18)",
                    borderRadius: "14px",
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <MDBox
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      borderBottom: "1px solid rgba(249,96,48,0.12)",
                      background:
                        "linear-gradient(90deg, rgba(249,96,48,0.08), transparent)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <MDBox sx={{ minWidth: 0, pr: { sm: 1 } }}>
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          sx={{ color: "#3B0050", display: "block" }}
                        >
                          {isEditTag
                            ? "Alterar valor da tag"
                            : "Adicionar tag ao lote"}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {isEditTag
                            ? "Ajuste o valor e confirme a alteração"
                            : "Escolha uma tag, informe o valor e adicione"}
                        </MDTypography>
                      </MDBox>

                      {!isEditTag && (
                        <MDButton
                          size="small"
                          variant={showCreateTagForm ? "outlined" : "gradient"}
                          color="dark"
                          onClick={() => {
                            if (showCreateTagForm) {
                              resetCreateTagForm();
                            } else {
                              setShowCreateTagForm(true);
                            }
                          }}
                          startIcon={
                            showCreateTagForm ? (
                              <CloseIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          sx={{
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            ...(!showCreateTagForm
                              ? {
                                  background: `linear-gradient(195deg, ${TAG_ACCENT}, #d94f28)`,
                                }
                              : {
                                  borderColor: "rgba(59,0,80,0.25)",
                                  color: "#3B0050",
                                }),
                          }}
                        >
                          {showCreateTagForm
                            ? "Cancelar"
                            : "Criar nova tag"}
                        </MDButton>
                      )}
                    </Stack>
                  </MDBox>

                  <MDBox
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {isEditTag && selectTag && (
                      <Alert
                        severity="warning"
                        sx={{ borderRadius: "10px" }}
                        action={
                          <MDButton
                            size="small"
                            color="dark"
                            variant="text"
                            onClick={resetTagForm}
                          >
                            Cancelar
                          </MDButton>
                        }
                      >
                        Editando <b>{selectTag.tipo}</b>
                      </Alert>
                    )}

                    {!isEditTag && showCreateTagForm && (
                      <MDBox
                        sx={{
                          flexShrink: 0,
                          p: 2,
                          borderRadius: "12px",
                          border: "1px solid rgba(249,96,48,0.25)",
                          bgcolor: "rgba(249,96,48,0.04)",
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          color="text"
                          display="block"
                          mb={1.5}
                        >
                          Nova tag do catálogo
                        </MDTypography>
                        <Grid container spacing={1.5} alignItems="flex-end">
                          <Grid item xs={12} sm={6}>
                            <FormField
                              name="newTagName"
                              type="text"
                              label="Nome da nova tag"
                              placeholder="Ex.: Especial, Duo..."
                              value={newTagName}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setNewTagName(e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              select
                              name="newTagQtd"
                              label="Tipo de inscrição"
                              value={newTagQtd}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setNewTagQtd(e.target.value)
                              }
                            >
                              {OPTIONTIPOINSCRICAO.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </FormField>
                          </Grid>
                          <Grid item xs={12}>
                            <MDTypography
                              variant="caption"
                              color="text"
                              fontWeight="medium"
                              display="block"
                              mb={1}
                            >
                              Cor da tag
                            </MDTypography>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                              alignItems="center"
                            >
                              {colorsCupons.map((color) => {
                                const selected = newTagCor === color;
                                return (
                                  <Box
                                    key={color}
                                    onClick={() => setNewTagCor(color)}
                                    title={color}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      bgcolor: color,
                                      cursor: "pointer",
                                      border: selected
                                        ? "2px solid #fff"
                                        : "2px solid transparent",
                                      outline: selected
                                        ? `2px solid ${color}`
                                        : "2px solid transparent",
                                      boxShadow: selected
                                        ? `0 0 0 2px ${color}`
                                        : "none",
                                      transition: "transform 0.15s",
                                      "&:hover": { transform: "scale(1.08)" },
                                    }}
                                  />
                                );
                              })}
                              <TagColorChip
                                tipo={newTagName || "Prévia"}
                                cor={newTagCor}
                                label={newTagName.trim() || "Prévia"}
                              />
                            </Stack>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <MDButton
                              variant="gradient"
                              color="dark"
                              fullWidth
                              disabled={creatingTag || !newTagName.trim()}
                              onClick={handleCreateTipoTag}
                              startIcon={
                                creatingTag ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <AddCircleOutlineIcon />
                                )
                              }
                              sx={{
                                height: 44,
                                background: `linear-gradient(195deg, ${TAG_ACCENT}, #d94f28)`,
                              }}
                            >
                              {creatingTag ? "Criando..." : "Criar"}
                            </MDButton>
                          </Grid>
                        </Grid>
                      </MDBox>
                    )}

                    {!isEditTag && (
                      <MDBox
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                          >
                            {showDeleteTagsMode
                              ? "Modo exclusão — clique no X para remover do catálogo"
                              : "Tags disponíveis — clique para selecionar"}
                          </MDTypography>
                          <MDButton
                            size="small"
                            variant={
                              showDeleteTagsMode ? "contained" : "outlined"
                            }
                            color={showDeleteTagsMode ? "error" : "dark"}
                            onClick={() =>
                              setShowDeleteTagsMode((prev) => !prev)
                            }
                            startIcon={
                              showDeleteTagsMode ? (
                                <DoneOutlinedIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <DeleteOutlineOutlinedIcon
                                  sx={{ fontSize: 16 }}
                                />
                              )
                            }
                            sx={{
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                              minHeight: 30,
                              px: 1.25,
                              ...(showDeleteTagsMode
                                ? {}
                                : {
                                    borderColor: "rgba(59,0,80,0.25)",
                                    color: "#3B0050",
                                  }),
                            }}
                          >
                            {showDeleteTagsMode
                              ? "Concluir"
                              : "Excluir tags"}
                          </MDButton>
                        </Stack>
                        <MDBox
                          sx={{
                            flex: 1,
                            minHeight: {
                              xs: showCreateTagForm ? 140 : 220,
                              md: showCreateTagForm ? 180 : 280,
                            },
                            display: "flex",
                            flexWrap: "wrap",
                            alignContent: "flex-start",
                            gap: 1,
                            overflowY: "auto",
                            p: 1.5,
                            borderRadius: "12px",
                            bgcolor: showDeleteTagsMode
                              ? "rgba(211,47,47,0.04)"
                              : "rgba(59,0,80,0.03)",
                            border: showDeleteTagsMode
                              ? "1px dashed rgba(211,47,47,0.35)"
                              : "1px dashed rgba(59,0,80,0.15)",
                          }}
                        >
                          {listTag.length === 0 ? (
                            <MDBox
                              sx={{
                                width: "100%",
                                height: "100%",
                                minHeight: 120,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                px: 2,
                              }}
                            >
                              <MDTypography variant="caption" color="text">
                                Nenhuma tag cadastrada. Use “Criar nova tag” no
                                topo do card.
                              </MDTypography>
                            </MDBox>
                          ) : (
                            listTag.map((item) => {
                              const selected = selectTag?.id === item.id;
                              const alreadyAdded = tagsAlreadyAdded.has(
                                item.id
                              );
                              const isDeleting = deletingTagId === item.id;
                              const tagColor = resolveCorTag(item);
                              return (
                                <Chip
                                  key={item.id}
                                  label={
                                    isDeleting ? "Excluindo..." : item.tipo
                                  }
                                  icon={
                                    alreadyAdded && !isDeleting ? (
                                      <CheckCircleIcon
                                        sx={{
                                          fontSize: "20px !important",
                                          color: "#00A53C !important",
                                          ml: "6px !important",
                                        }}
                                      />
                                    ) : undefined
                                  }
                                  onClick={() =>
                                    !isDeleting &&
                                    !showDeleteTagsMode &&
                                    handleSelectTag(item)
                                  }
                                  onDelete={
                                    showDeleteTagsMode && !isDeleting
                                      ? (event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          void handleDeleteTipoTag(item);
                                        }
                                      : undefined
                                  }
                                  disabled={isDeleting}
                                  variant={selected ? "filled" : "outlined"}
                                  sx={{
                                    fontWeight: selected ? 700 : 500,
                                    color: selected ? "#fff" : "#3B0050",
                                    border: `1.5px solid ${
                                      alreadyAdded && !selected
                                        ? "#00A53C"
                                        : tagColor
                                    }`,
                                    bgcolor: selected
                                      ? tagColor
                                      : alreadyAdded
                                        ? "rgba(0,165,60,0.08)"
                                        : "#fff",
                                    cursor: showDeleteTagsMode
                                      ? "default"
                                      : "pointer",
                                    "& .MuiChip-icon": {
                                      marginLeft: "8px",
                                      marginRight: "-4px",
                                    },
                                    "& .MuiChip-deleteIcon": {
                                      color: "rgba(211,47,47,0.85)",
                                      "&:hover": {
                                        color: "#d32f2f",
                                      },
                                    },
                                    "&:hover": {
                                      bgcolor: selected
                                        ? tagColor
                                        : alreadyAdded
                                          ? "rgba(0,165,60,0.14)"
                                          : `${tagColor}18`,
                                      borderColor:
                                        alreadyAdded && !selected
                                          ? "#00A53C"
                                          : tagColor,
                                    },
                                  }}
                                />
                              );
                            })
                          )}
                        </MDBox>
                      </MDBox>
                    )}

                    <MDBox
                      sx={{
                        flexShrink: 0,
                        mt: isEditTag ? "auto" : 0,
                        pt: 2,
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={12} sm={7}>
                          <FormField
                            name="valorTag"
                            type="text"
                            label={
                              selectTag
                                ? `Valor — ${selectTag.tipo}`
                                : "Valor da tag"
                            }
                            value={valueTag}
                            onChange={handleValueTag}
                            disabled={!selectTag}
                            placeholder="0,00"
                            inputProps={{ inputMode: "numeric" }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  R$
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <MDButton
                            disabled={!selectTag || savingTag}
                            variant="gradient"
                            color={isEditTag ? "info" : "dark"}
                            fullWidth
                            onClick={handleAddTag}
                            sx={{
                              height: 44,
                              ...(!isEditTag
                                ? {
                                    background: `linear-gradient(195deg, ${TAG_ACCENT}, #d94f28)`,
                                  }
                                : {}),
                            }}
                          >
                            {savingTag
                              ? "Salvando..."
                              : isEditTag
                                ? "Salvar valor"
                                : "Adicionar ao lote"}
                          </MDButton>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          {/* Footer fixo */}
          <MDBox
            display="flex"
            justifyContent="flex-end"
            gap={1.5}
            px={{ xs: 2.5, md: 3.5 }}
            py={2}
            sx={{
              flexShrink: 0,
              borderTop: "1px solid rgba(59,0,80,0.08)",
              bgcolor: "#fff",
            }}
          >
            <MDButton
              variant="outlined"
              color="dark"
              onClick={handleCloseModal}
              sx={{ minWidth: 120 }}
            >
              Cancelar
            </MDButton>
            {isEdit ? (
              <MDButton
                variant="gradient"
                color="info"
                onClick={submitFormAddLote}
                sx={{ minWidth: 200 }}
              >
                Salvar alterações
              </MDButton>
            ) : (
              <MDButton
                variant="gradient"
                color="success"
                onClick={submitFormAddLote}
                sx={{ minWidth: 200 }}
              >
                Criar lote
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      </Fade>
    </Modal>
  );
}

const styleModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  height: "auto",
  maxHeight: "92vh",
  overflow: "hidden",
  bgcolor: "#f8f7fa",
  borderRadius: "16px",
  boxShadow: "0 24px 64px rgba(59,0,80,0.22)",
  outline: "none",
};

ModalAddLote.defaultProps = {
  isEdit: false,
};
